import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, RefreshCw, Users, MousePointerClick, Heart, MessageSquare } from 'lucide-react';
import Header from '../components/Header';
import { API_BASE } from '../lib/api';

function useMetrics(range) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const token = localStorage.getItem('habita_token');
    fetch(`${API_BASE}/advisor/admin/metrics?range=${range}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((j) => { if (!cancel) setData(j); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [range, tick]);
  return { data, loading, reload: () => setTick((t) => t + 1) };
}

function KPI({ label, value, sub, icon: Icon, tone = 'default' }) {
  const toneCls = tone === 'positive' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-700' : 'text-habita-dark';
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4">
      <div className="flex items-center gap-2 text-gray-500 text-[11px] uppercase tracking-widest">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <p className={`mt-2 font-serif text-2xl font-bold ${toneCls}`}>{value}</p>
      {sub && <p className="mt-1 text-[11px] text-gray-500">{sub}</p>}
    </div>
  );
}

function FunnelBar({ label, value, base, testid }) {
  const pct = base > 0 ? Math.min(100, (value / base) * 100) : 0;
  return (
    <div className="mb-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm text-habita-dark font-medium">{label}</p>
        <p className="text-xs text-gray-500">
          <strong className="text-habita-dark">{value.toLocaleString()}</strong>
          {base > 0 && <span className="ml-2">({pct.toFixed(1)}%)</span>}
        </p>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-habita rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function AdvisorMetricsPage() {
  const [range, setRange] = useState('7d');
  const { data, loading, reload } = useMetrics(range);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8">
        <Link to="/admin/advisor" className="text-sm text-gray-500 hover:text-habita inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Volver al Inspector
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-serif text-3xl font-bold text-habita-dark">
              Habita Advisor · Panel de métricas
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Validación del MVP · embudo, negocio y salud del motor.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {['24h', '7d', '30d'].map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                data-testid={`range-${r}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${range === r ? 'bg-habita text-white border-habita' : 'bg-white text-gray-700 border-gray-200 hover:border-habita'}`}
              >{r}</button>
            ))}
            <button onClick={reload} className="p-1.5 rounded-full text-gray-500 hover:text-habita">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading && <div className="h-64 rounded-2xl bg-gray-100 animate-pulse" />}

        {!loading && data && (
          <div className="space-y-6">
            {/* Business KPIs */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500 mb-3">Impacto en el usuario</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="business-kpis">
                <KPI label="Usuarios que interactúan" value={`${data.business.interacting_users_pct}%`}
                     sub={`${data.business.interacting_users} de ${data.business.active_users}`} icon={Users} tone="positive" />
                <KPI label="Contactos influenciados" value={`${data.business.contacts_influenced_pct}%`}
                     sub={`${data.business.contacts_influenced_by_advisor} de ${data.business.contacts_total}`} icon={MessageSquare} />
                <KPI label="Favoritos desde Advisor" value={`${data.business.favorites_influenced_pct}%`}
                     sub={`${data.business.favorites_from_advisor} de ${data.business.favorites_total}`} icon={Heart} />
                <KPI label="Tiempo hasta decidir (p50)"
                     value={data.time_to_decision ? `${data.time_to_decision.p50_seconds}s` : '—'}
                     sub={data.time_to_decision ? `${data.time_to_decision.samples} muestras` : 'sin datos'}
                     icon={MousePointerClick} />
              </div>
            </div>

            {/* Funnel */}
            <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="funnel-card">
              <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500 mb-4">Embudo de conversión</h2>
              <FunnelBar label="Recomendaciones generadas" value={data.funnel.generated} base={data.funnel.generated} testid="funnel-generated" />
              <FunnelBar label="Mostradas al usuario" value={data.funnel.shown} base={data.funnel.generated} testid="funnel-shown" />
              <FunnelBar label="Explicación abierta" value={data.funnel.explanation_opened} base={data.funnel.shown} testid="funnel-explained" />
              <FunnelBar label="Click en la recomendación" value={data.funnel.clicked} base={data.funnel.shown} testid="funnel-clicked" />
              <FunnelBar label="Terminó en contacto" value={data.funnel.contacted} base={data.funnel.shown} testid="funnel-contacted" />
              <FunnelBar label="Descartadas" value={data.funnel.dismissed} base={data.funnel.shown} testid="funnel-dismissed" />
            </div>

            {/* Health */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500 mb-3">Salud del motor</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KPI label="Tiempo promedio" value={`${data.health.avg_elapsed_ms}ms`} sub={`${data.health.under_500ms_pct}% <500ms`} icon={TrendingUp} />
                <KPI label="Recomendaciones vacías" value={data.health.empty_results_count} sub={`de ${data.health.total_generated} totales`} tone={data.health.empty_results_count > 0 ? 'warn' : 'default'} />
                <KPI label="Candidatos evaluados" value={data.health.avg_candidates_evaluated.toFixed(0)} sub="promedio por recomendación" />
                <KPI label="Score promedio" value={data.health.avg_score_when_generated.toFixed(2)} sub="cuando se genera" />
              </div>
            </div>

            {/* By strategy + top types + cities */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-habita-dark mb-3">Estrategias en uso</h3>
                <div className="space-y-2">
                  {Object.entries(data.by_strategy).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-sm">
                      <span className="font-mono text-xs bg-habita-mint/40 px-2 py-0.5 rounded">{k}</span>
                      <span className="text-gray-600">{v.count} · {v.avg_elapsed_ms.toFixed(1)}ms</span>
                    </div>
                  ))}
                  {Object.keys(data.by_strategy).length === 0 && (
                    <p className="text-xs text-gray-400">Sin datos aún</p>
                  )}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-habita-dark mb-3">Tipos con más interacción</h3>
                <div className="space-y-2">
                  {data.top_types.map((t) => (
                    <div key={t.type} className="flex items-center justify-between text-sm">
                      <span className="capitalize">{t.type}</span>
                      <span className="text-gray-600">{t.count} · avg {(t.avg_score * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                  {data.top_types.length === 0 && <p className="text-xs text-gray-400">Sin datos aún</p>}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-habita-dark mb-3">Ciudades destacadas</h3>
                <div className="space-y-2">
                  {data.top_cities.map((c) => (
                    <div key={c.city} className="flex items-center justify-between text-sm">
                      <span>{c.city}</span>
                      <span className="text-gray-600">{c.count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <h3 className="text-sm font-semibold text-habita-dark mb-3">Explicaciones más abiertas</h3>
                <div className="space-y-2">
                  {data.top_explanations?.length ? data.top_explanations.map((e) => (
                    <div key={e.strategy} className="flex items-center justify-between text-sm">
                      <span className="font-mono text-xs">{e.strategy}</span>
                      <span className="text-gray-600">{e.openings}</span>
                    </div>
                  )) : <p className="text-xs text-gray-400">Sin datos aún</p>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
